import React, { useState } from 'react';
import { Tooltip } from 'antd';
import s from './s.module.less';
import AiChatContent from './components/AiChatContent';
import { EAIChatPageId } from 'types/ai';
import { isMobile, isOpPortal } from 'utils/common';
import useSettings from 'hooks/useSettings';
import { EnumFields as SettingEnumFields, SettingEnum, SettingDataType, EnumFields } from 'types/enumerationData';
import { getAdminListSetting, getListSetting } from 'api/common';
import SettingEnumDataStore from 'store/Enumeration';
import { TAiChatConfig } from 'types/aiChat';

type Props = {
    defaultShow?: boolean;
    buttonStyle?: React.CSSProperties;
    onVisableChange?: (show: boolean) => void;
    height?: string;
    width?: string;
    pageId?: EAIChatPageId;
    makeFix?: boolean;
    initialMessage?: string;
    // 自定义内容渲染函数，用于在特定页面替换默认聊天内容
    renderContent?: (options: { onClose: () => void }) => React.ReactNode;
};

type TVideoConfig = {
    title: string;
    subtitle: string;
    src: string;
};

const getVideoConfigByPageId = (pageId?: EAIChatPageId): TVideoConfig | null => {
    // PPA Lead Form 第一步：邮箱填写，展示 billing 介绍视频
    // if (pageId === EAIChatPageId.PROVIDER_ONBOARDING_EMAIL_ENTRY) {
    //     return {
    //         title: 'Getting started with Klarity',
    //         subtitle:
    //             'Please watch this quick video to learn how  Klarity works, what makes it different, and what to expect next.',
    //         src: 'https://www.youtube.com/embed/oBMpal90Jq8',
    //     };
    // }

    // PPA Onboarding Steps - List on Klarity Health Step1：展示上架 Klarity Health 的说明视频
    if (pageId === EAIChatPageId.PROVIDER_ONBOARDING_KLARITY_INTRO) {
        return {
            title: 'Understanding Klarity billing',
            subtitle:
                'Please watch this short video to understand how billing works on Klarity, including charges, invoicing, and payment details.',
            src: 'https://www.youtube.com/embed/y75Uq5t747k',
        };
    }

    return null;
};

const VideoWrap: React.FC<{ config: TVideoConfig; onClose?: () => void }> = ({ config, onClose }) => {
    return (
        <div className={s.videoWrap}>
            <div className={s.videoHeader}>
                <div className={s.videoText}>
                    <div className={s.videoTitle}>{config.title}</div>
                    <div className={s.videoSubtitle}>{config.subtitle}</div>
                </div>
                {onClose && (
                    <button type="button" className={s.videoClose} onClick={onClose} aria-label="Close">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                                d="M1 13L13 1M1 1L13 13"
                                stroke="#4B5563"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                )}
            </div>
            <div className={s.videoFrame}>
                <iframe
                    src={config.src}
                    title={config.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            </div>
        </div>
    );
};

export default function AIChat({
    defaultShow = !isMobile(),
    buttonStyle,
    makeFix = false,
    onVisableChange,
    height,
    width,
    pageId,
    initialMessage,
    renderContent,
}: Props) {
    const [show, setShow] = useState(defaultShow);
    const [showAnimation, setShowAnimation] = useState(false);
    const [fix, setFix] = useState(false);
    const [, setSettingEnumData] = SettingEnumDataStore.useStore();
    const [setting] = useSettings();
    const aiChatOption = setting[EnumFields.CHATBOT_CONFIG] || [];
    const onboardingChatConfigStr = aiChatOption.find((item) => item.value === 'provider-onboarding');
    let onboardingChatConfig: TAiChatConfig | undefined;
    if (onboardingChatConfigStr) {
        try {
            onboardingChatConfig = JSON.parse(onboardingChatConfigStr.label);
        } catch (e) {
            console.error(e);
        }
    }
    const showChatbot = onboardingChatConfig && onboardingChatConfig?.enabled;

    const loadListSetting = async () => {
        let result;
        if (isOpPortal()) {
            result = await getAdminListSetting();
        } else {
            result = await getListSetting();
        }
        if (!result?.error) {
            const { data } = result.data;
            const settingEnum: SettingEnumFields[] = [];
            const initData: Partial<SettingDataType> = {};

            // eslint-disable-next-line guard-for-in
            for (const key in SettingEnumFields) {
                settingEnum.push(key as SettingEnumFields);
                initData[key as EnumFields] = [];
            }

            const enumData = settingEnum.reduce((res: SettingDataType, key) => {
                res[key] = data?.filter((item: SettingEnum) => item.type === key);

                return res;
            }, initData as SettingDataType);

            setSettingEnumData('data', enumData);
        }
    };

    React.useEffect(() => {
        loadListSetting();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (show) {
            setTimeout(() => {
                setShowAnimation(true);
                if (makeFix) {
                    setTimeout(() => {
                        setFix(true);
                    }, 200);
                }
            }, 1000);
        }
        onVisableChange?.(show);
    }, [show, onVisableChange, makeFix]);

    if (!showChatbot) {
        //hide for production
        return null;
    }

    const onClose = () => {
        setShowAnimation(false);
        setTimeout(() => {
            setShow(false);
        }, 200);
    };

    const onShow = () => {
        setShow(true);
        setShowAnimation(true);
    };

    let cssStyle: React.CSSProperties | undefined;
    if (height || width) {
        cssStyle = {};
        if (height) {
            cssStyle.height = height;
        }
        if (width) {
            cssStyle.width = width;
        }
    }

    if (!show) {
        return (
            <Tooltip
                title="Ask Agent"
                placement="top"
                color="#FFFFFF"
                overlayInnerStyle={{
                    color: 'var(--Primary-Black, #15191E)',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                }}
            >
                <div className={s.btn} onClick={onShow} style={buttonStyle || undefined}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.4998 1.7998C4.99686 1.7998 5.3998 2.20275 5.3998 2.6998V3.5998H6.2998C6.79686 3.5998 7.1998 4.00275 7.1998 4.4998C7.1998 4.99686 6.79686 5.3998 6.2998 5.3998H5.3998V6.2998C5.3998 6.79686 4.99686 7.1998 4.4998 7.1998C4.00275 7.1998 3.5998 6.79686 3.5998 6.2998V5.3998H2.6998C2.20275 5.3998 1.7998 4.99686 1.7998 4.4998C1.7998 4.00275 2.20275 3.5998 2.6998 3.5998H3.5998V2.6998C3.5998 2.20275 4.00275 1.7998 4.4998 1.7998ZM4.4998 10.7998C4.99686 10.7998 5.3998 11.2027 5.3998 11.6998V12.5998H6.2998C6.79686 12.5998 7.1998 13.0027 7.1998 13.4998C7.1998 13.9969 6.79686 14.3998 6.2998 14.3998H5.3998V15.2998C5.3998 15.7969 4.99686 16.1998 4.4998 16.1998C4.00275 16.1998 3.5998 15.7969 3.5998 15.2998V14.3998H2.6998C2.20275 14.3998 1.7998 13.9969 1.7998 13.4998C1.7998 13.0027 2.20275 12.5998 2.6998 12.5998H3.5998V11.6998C3.5998 11.2027 4.00275 10.7998 4.4998 10.7998Z"
                            fill="white"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.7998 1.7998C11.2081 1.7998 11.5653 2.07474 11.6698 2.4695L12.7311 6.47884L15.7495 8.22024C16.0281 8.38097 16.1998 8.67816 16.1998 8.9998C16.1998 9.32145 16.0281 9.61864 15.7495 9.77937L12.7311 11.5208L11.6698 15.5301C11.5653 15.9249 11.2081 16.1998 10.7998 16.1998C10.3914 16.1998 10.0342 15.9249 9.92972 15.5301L8.86843 11.5208L5.85005 9.77937C5.57145 9.61864 5.3998 9.32144 5.3998 8.9998C5.3998 8.67816 5.57145 8.38097 5.85005 8.22024L8.86843 6.47885L9.92972 2.4695C10.0342 2.07474 10.3914 1.7998 10.7998 1.7998Z"
                            fill="white"
                        />
                    </svg>
                </div>
            </Tooltip>
        );
    }

    // 优先使用外部传入的 renderContent，其次根据 pageId 内置 VideoWrap，最后回退到默认聊天内容
    const internalVideoConfig = getVideoConfigByPageId(pageId);

    let contentNode: React.ReactNode;
    if (renderContent) {
        contentNode = renderContent({ onClose });
    } else if (internalVideoConfig) {
        contentNode = <VideoWrap config={internalVideoConfig} onClose={onClose} />;
    } else {
        contentNode = <AiChatContent pageId={pageId} onClose={onClose} initialMessage={initialMessage} />;
    }

    return (
        <>
            <div className={`${s.wrap} ${showAnimation ? s.wrapShow : ''} ${fix ? s.wrapFix : ''}`} style={cssStyle}>
                {contentNode}
            </div>
            <div className={`${s.placeHolder} ${fix ? s.placeHolderFix : ''}`} />
        </>
    );
}
